import {
  DraggableProvidedDraggableProps,
  DraggableProvidedDragHandleProps,
} from "@hello-pangea/dnd";
import {
  Box,
  Button,
  IconButton,
  ListItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { forwardRef } from "react";
import {
  GFE_SERVICE_STATUS_ICON,
  mapToGfeChipType,
} from "@/components/common/gfe/gfeIndicationChip";
import {
  DollarCircleStrokeStandardIcon,
  DragIndicatorIcon,
  TrashIcon,
  UserIcon,
} from "@/components/common/icons";
import { CamIcon } from "@/components/common/icons";
import { getVariablePricingCopy } from "@/components/serviceFlow/common/groupedServicesSelectionList";
import {
  ERROR_PRIMARY,
  GREY,
  PRIMARY,
  VIOLET,
} from "@/config/mui/colorPalette";
import { ServicesForBookingAppointmentQuery } from "@/graphql/queries/appointment/servicesForBookingAppointment.graphql.types";
import { AppointmentType } from "@/types";
import { formattedPrice } from "@/utils";
import { Provider } from "../providerSection";
import { AvailableService } from "./types";

type EligibleProvider =
  ServicesForBookingAppointmentQuery["medspaServiceMenuItem"][number]["eligibleProviders"][number];

type SelectedServiceProps = {
  service: AvailableService;
  provider: Provider;
  onRemove?: (id: string) => void;
  onEditProvider?: () => void;
  isLast?: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  draggableProps?: DraggableProvidedDraggableProps;
  isDragging?: boolean;
};

export const SelectedService = forwardRef<HTMLLIElement, SelectedServiceProps>(
  (
    {
      service,
      provider,
      onRemove,
      onEditProvider,
      dragHandleProps,
      draggableProps,
      isLast,
      isDragging,
    },
    ref
  ) => (
    <ListItem
      ref={ref}
      component="li"
      sx={{
        backgroundColor: isDragging ? VIOLET[40] : VIOLET[20],
        borderRadius: 2,
        p: 1.5,
        pl: dragHandleProps ? 0 : 1.5,
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        mb: isLast ? 0 : 1.5,
      }}
      {...draggableProps}
    >
      {dragHandleProps && (
        <Box
          {...dragHandleProps}
          sx={{
            px: 0.5,
            display: "inline-flex",
            cursor: "grab",
            alignSelf: "stretch",
            "& > svg": {
              transition: (theme) =>
                theme.transitions.create("background-color"),
              padding: 1,
              marginTop: "-8px",
              borderRadius: 2,
            },
            "&:hover > svg": {
              backgroundColor: VIOLET[40],
            },
          }}
        >
          <DragIndicatorIcon size={40} color={PRIMARY} strokeWidth={3} />
        </Box>
      )}
      <Stack gap={1} sx={{ overflow: "hidden", flex: 1 }}>
        <ServiceRow service={service} onRemove={onRemove} />
        <PriceRow service={service} />
        <ProviderRow
          provider={provider}
          eligibleProviders={service.eligibleProviders}
          onEditProvider={onEditProvider}
        />
      </Stack>
    </ListItem>
  )
);

SelectedService.displayName = "SelectedService";

const ServiceRow = ({
  service,
  onRemove,
}: {
  service: AvailableService;
  onRemove?: (id: string) => void;
}) => {
  const GfeIcon =
    GFE_SERVICE_STATUS_ICON[mapToGfeChipType[service.clientGfeIndication]];

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="start"
      gap={0.5}
    >
      <Stack direction="row" gap={0.5} alignItems="center">
        {service.appointmentType === AppointmentType.TELEHEALTH && (
          <CamIcon
            color={PRIMARY}
            style={{ flexShrink: 0, alignSelf: "flex-start", marginTop: 2 }}
          />
        )}
        <Typography
          variant="labelDefault"
          color="text.primary"
          fontWeight={600}
        >
          {service.name}
        </Typography>
      </Stack>
      <Stack direction="row" gap={1} alignItems="center">
        {GfeIcon && <GfeIcon />}
        {onRemove && (
          <IconButton
            onClick={() => onRemove(service.id)}
            color="primary"
            sx={{
              p: 0,
              "& > svg": {
                transition: (theme) => theme.transitions.create("color"),
              },
              "&:hover > svg": {
                color: ERROR_PRIMARY,
              },
            }}
          >
            <TrashIcon color={GREY[60]} strokeWidth={1.5} size={24} />
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
};

const PriceRow = ({ service }: { service: AvailableService }) => {
  return (
    <Stack direction="row" gap={0.5} alignItems="center">
      <DollarCircleStrokeStandardIcon
        color={GREY[60]}
        size={20}
        style={{ marginTop: "-2px" }}
      />
      <Typography variant="paragraphSmall">
        {service.isVariablePricing
          ? getVariablePricingCopy(service.price)
          : formattedPrice(service.price)}
      </Typography>
    </Stack>
  );
};

const ProviderRow = ({
  provider,
  eligibleProviders,
  onEditProvider,
}: {
  provider: Provider;
  eligibleProviders: EligibleProvider[];
  onEditProvider?: () => void;
}) => {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      gap={1}
      alignItems="center"
      mt="-4px" // visually align to Figma
    >
      <Stack
        direction="row"
        gap={0.5}
        sx={{ overflow: "hidden", flex: "1 1 auto" }}
      >
        <UserIcon
          size="20px"
          color={GREY[60]}
          style={{ flexShrink: 0, marginTop: "-2px" }} // visually align to Figma
        />
        <Typography
          variant="paragraphSmall"
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={provider.user.fullName}
        >
          {provider.user.fullName}
        </Typography>
      </Stack>
      {onEditProvider && (
        <EditProviderButton
          provider={provider}
          eligibleProviders={eligibleProviders}
          onEditProvider={onEditProvider}
        />
      )}
    </Stack>
  );
};

const EditProviderButton = ({
  provider,
  eligibleProviders,
  onEditProvider,
}: {
  provider: Provider;
  eligibleProviders: EligibleProvider[];
  onEditProvider: () => void;
}) => {
  const hasSingleEligibleProvider = eligibleProviders.length === 1;
  const button = (
    <Button
      variant="text"
      size="small"
      color="primary"
      disabled={hasSingleEligibleProvider}
      sx={{ whiteSpace: "nowrap", height: "auto", flexShrink: 0 }}
      onClick={onEditProvider}
    >
      Edit provider
    </Button>
  );

  return hasSingleEligibleProvider ? (
    <Tooltip
      title={
        <Typography variant="paragraphTiny" color="white">
          {provider.user.fullName} is the only
          <br /> one provider who can perform this service.
        </Typography>
      }
      placement="top-end"
      arrow
    >
      {/**
       * From tooltip docs: https://mui.com/material-ui/react-tooltip/#disabled-elements
       * "In order to work with Safari, you need at least one display block or
       * flex item below the tooltip wrapper.""
       */}
      <span>{button}</span>
    </Tooltip>
  ) : (
    button
  );
};
